import {
  ChangeEvent,
  cloneElement,
  FocusEvent,
  FormEvent,
  forwardRef,
  InputHTMLAttributes,
  MutableRefObject,
  ReactElement,
  useRef,
  useState,
} from 'react';
import { DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components';

import Modal from '~components/Modal';
import Text from '~components/Text';
import useGetCompanyAutoComplete from '~hooks/query/company/useCompanyAutoComplete';
import { KeysOf } from '~types/common';
import { ICompany } from '~types/company';
import { FirstStepValue } from '~types/salaryreport';
import { getShortenedAddress } from '~utils/locationFormat';

import { getHighlightedText } from './helper';
import { S } from './style';

export interface AutoSuggestProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onSubmit'> {
  value: ICompany;
  setValue?: (company: ICompany) => void;
  handleChange?: (value: ICompany, type: KeysOf<FirstStepValue>, query?: string) => void;
  renderInput: ReactElement;
  inlineCss?: FlattenInterpolation<ThemeProps<DefaultTheme>>;
  listCss?: FlattenInterpolation<ThemeProps<DefaultTheme>>;
  ref?: MutableRefObject<HTMLDivElement>;
  isForm?: boolean;
  onSubmit?: (company: ICompany, query?: string) => void;
}

const AutoSuggest = forwardRef<HTMLDivElement, AutoSuggestProps>(
  (
    { value, setValue, handleChange, renderInput, inlineCss, listCss, onBlur, onSubmit, isForm },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [search, setSearch] = useState('');
    const [showSuggestion, setShowSuggetion] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const { data, status } = useGetCompanyAutoComplete(search);

    return (
      <S.Wrapper inlineCss={inlineCss}>
        {cloneElement(renderInput, {
          ref: inputRef,
          onKeyPress: (e) => {
            if (e.key === 'Enter' && isForm) {
              if (data && data.docs.length > 0) {
                const company = data.docs[0];
                setShowSuggetion(false);
                if (onSubmit) {
                  onSubmit(company, search);
                }

                e.target.blur();

                return;
              }

              if (search.length !== 0 && status === 'success') {
                setIsOpen(true);
              } else {
                e.target.blur();
              }
            }
          },
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            if (renderInput.props.onChange) {
              renderInput.props.onChange();
            }
            if (showSuggestion) {
              setSearch(e.target.value);

              return;
            }

            setShowSuggetion(true);
            setSearch(e.target.value);
          },
          onBlur: (e: FocusEvent<HTMLInputElement>) => {
            if (renderInput.props.onBlur) {
              renderInput.props.onBlur();
            }
            if (e.target.value !== value?.name) {
              if (setValue) {
                setValue({} as ICompany);
                if (onBlur) {
                  onBlur(e);
                }
              }
            }
            setShowSuggetion(false);
          },
          onFocus: () => {
            if (renderInput.props.onFocus) {
              renderInput.props.onFocus();
            }
            if (showSuggestion) {
              return;
            }
            setShowSuggetion(true);
          },
        })}
        {showSuggestion && data && data.docs.length > 0 && (
          <S.CompanyListWrapper listCss={listCss} ref={ref}>
            <S.CompanyListTitle>
              <Text typography='midium150_C1'>검색결과</Text>
            </S.CompanyListTitle>
            <S.CompanyList>
              {data.docs.map((company: ICompany) => (
                <S.CompanyInfo
                  key={company.PK_NM_HASH}
                  onMouseDown={() => {
                    if (setValue) {
                      setValue(company);
                    }

                    setSearch(company.name);
                    setShowSuggetion(false);

                    if (handleChange) {
                      handleChange(company, 'company', search);
                    }
                  }}>
                  {getHighlightedText(company.name, search)}{' '}
                  <span>{`/ ${getShortenedAddress(company.locationCode, company.location)}`}</span>
                </S.CompanyInfo>
              ))}
            </S.CompanyList>
          </S.CompanyListWrapper>
        )}
        <Modal
          isOpen={isOpen}
          title='검색 결과가 없습니다.'
          subTitle='검색어를 변경해보세요.'
          confirmButtonText='다시 검색하기'
          handleConfirm={() => {
            setIsOpen(false);

            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
          handleClose={() => setIsOpen(true)}
          confirmButtonVariant='dark'
        />
      </S.Wrapper>
    );
  },
);

AutoSuggest.displayName = 'autoSuggest';

export default AutoSuggest;
